import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { redesignFlags } from '../featureFlags';
import type { AdminChurchInfo } from '../types';
import { AuthScreen, AuthTitle } from '../components/auth/AuthScreen';
import { Spinner, cx, initialsOf } from '../components/ui';

export const SelectChurch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, switchChurch } = useAuth();
  const [switching, setSwitching] = useState<string | null>(null);

  // Optional return-to target carried over from Login (e.g. /upload).
  // Sanitized to internal paths only to avoid an open redirect.
  const rawRedirect = new URLSearchParams(location.search).get('redirect');
  const redirectTo =
    rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : null;

  const adminChurches: AdminChurchInfo[] = user?.adminChurches ?? [];

  const handleSelectChurch = async (churchId: string) => {
    setSwitching(churchId);
    try {
      await switchChurch(churchId);
      navigate(redirectTo ?? '/admin/dashboard', { replace: true });
    } catch {
      // Error already logged in AuthContext.
    } finally {
      setSwitching(null);
    }
  };

  // The session is restored from storage after the first render; wait for it,
  // or reloading this page would bounce a signed-in admin to /login.
  if (isLoading) return null;
  // If no churches or single church, redirect
  if (!user) return <Navigate to="/login" replace />;
  if (adminChurches.length <= 1) return <Navigate to={redirectTo ?? '/admin/dashboard'} replace />;

  return (
    <AuthScreen>
      <AuthTitle title="Choose a church" sub="You belong to more than one church" />
      <div className="w-full flex flex-col gap-2.5" style={{ animation: 'wpRise .8s .2s var(--ease) both' }}>
        {adminChurches.map((church, i) => {
          const current = church.churchId === user.churchId;
          return (
            <button
              key={church.churchId}
              type="button"
              disabled={!!switching}
              onClick={() => handleSelectChurch(church.churchId)}
              className="wp-press flex items-center gap-3.5 w-full text-left bg-card border border-line rounded-[18px] p-4 text-t1 hover:bg-card-h hover:border-[rgba(208,48,69,.35)] disabled:cursor-wait"
            >
              <span
                className={cx(
                  'w-11 h-11 rounded-[13px] font-bold text-[14px] flex items-center justify-center shrink-0',
                  i === 0 || current ? 'bg-acc-bg2 text-acc' : 'bg-fill2 text-t2',
                )}
                aria-hidden="true"
              >
                {initialsOf(church.churchName)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-semibold truncate">{church.churchName}</span>
                <span className="block text-[13px] text-t4 mt-0.5">
                  {church.role}
                  {current ? ' · current' : ''}
                </span>
              </span>
              {switching === church.churchId ? (
                <Spinner size={16} />
              ) : (
                <ChevronRight size={18} strokeWidth={2} className="text-t5 shrink-0" aria-hidden="true" />
              )}
            </button>
          );
        })}
        {/* Hidden: /create-church is behind RequireNoChurch, which sends anyone
            who already has a church to the dashboard. See redesignFlags. */}
        {redesignFlags.newChurchFromSelector && (
          <button
            type="button"
            onClick={() => navigate('/create-church')}
            className="flex items-center justify-center gap-2 w-full h-14 bg-transparent border border-dashed border-line2 rounded-[18px] text-t3 hover:text-t1 hover:border-t5 text-[14px] font-medium"
          >
            + Set up a new church
          </button>
        )}
      </div>
    </AuthScreen>
  );
};
